import { useI18n } from 'hooks/useI18n';
import { useRouter } from 'next/router';
import { Bar, BarChart, Tooltip, XAxis, YAxis } from 'recharts';
import type {
  SalesItemAttributes,
  SalesProps
} from '../../BattleReportComponent.types';
import { StyledChart } from '../goals/BattleReportGoals.styled';

type dataItem = {
  month: string;
  sales: number | null;
  turnover: number | null;
};

const QuarterChart: React.FC<SalesProps> = ({ sales }) => {
  const { locale } = useRouter();
  const { t } = useI18n('battle-report');

  const filterMonth = (
    month: number,
    property: keyof SalesItemAttributes
  ): number | null => {
    const filteredData = sales?.filter((obj) => {
      if (obj.attributes.month === month) {
        return obj;
      }
    });

    return filteredData.length
      ? (filteredData[0].attributes[property] as number)
      : null;
  };

  const getMonthAttributes = (month: number): number =>
    sales[month]?.attributes.month;

  const getMonthName = (monthNumber: number): string => {
    if (!monthNumber) {
      return '';
    }
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return date.toLocaleString(locale, { month: 'short' });
  };

  const chartData = (): dataItem[] => {
    const a = [];
    for (let i = 0; i < 3; i++) {
      a.push({
        month: getMonthName(getMonthAttributes(i)),
        sales: filterMonth(getMonthAttributes(i), 'sales'),
        turnover: filterMonth(getMonthAttributes(i), 'billing')
      });
    }
    return a;
  };

  return (
    <StyledChart>
      <BarChart data={chartData()} width={250} height={180}>
        <XAxis
          type="category"
          dataKey="month"
          tickLine={false}
          interval={0}
          axisLine={true}
        />
        <YAxis
          tickLine={false}
          width={60}
          axisLine={true}
          tickMargin={5}
          tickFormatter={(tick: number) => {
            return `€${(Math.sign(tick) * (Math.abs(tick) / 1000)).toFixed(
              0
            )}K`;
          }}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          labelStyle={{ display: 'none' }}
          separator=": "
          formatter={(value: number) => `${value.toLocaleString(locale)} €`}
          wrapperClassName="tooltip-x"
        />
        <Bar
          dataKey="sales"
          barSize={8}
          radius={[5, 5, 0, 0]}
          fill="#8DD3C7"
          name={t('battle-report.objectives.sales')}
        />
        <Bar
          dataKey="turnover"
          barSize={8}
          radius={[5, 5, 0, 0]}
          fill="#A6D855"
          name={t('battle-report.objectives.turnover')}
        ></Bar>
      </BarChart>
    </StyledChart>
  );
};

export default QuarterChart;
